import { useEffect, useState } from "react";
import type { Video } from "@/types/video";
import { typeLabels } from "@/utils/video";

interface PlayerModalProps {
  video: Video;
  onClose: () => void;
}

const qualities = ["1080P", "720P", "自适应"];

export default function PlayerModal({ video, onClose }: PlayerModalProps) {
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(6);
  const [quality, setQuality] = useState("1080P");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 100 : prev + 0.35));
    }, 500);
    return () => window.clearInterval(timer);
  }, [playing]);

  const formatTime = (ratio: number) => {
    const totalSeconds = 126 * 60;
    const current = Math.floor((totalSeconds * ratio) / 100);
    const minutes = Math.floor(current / 60);
    const seconds = current % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 md:p-6" role="dialog" aria-modal="true" aria-label={`${video.title}播放预览`}>
      <button
        type="button"
        aria-label="关闭播放器"
        onClick={onClose}
        className="absolute inset-0 bg-background-50/90 backdrop-blur-md"
      />

      <div className="relative z-10 w-full max-w-4xl animate-fade-up overflow-hidden rounded-lg border border-background-300/60 bg-background-100">
        <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary-500/20 text-primary-400">
              <i className="ri-play-fill text-sm" />
            </span>
            <p className="truncate font-heading text-sm text-foreground-900">
              {video.title}
              <span className="ml-2 font-body text-xs text-foreground-500">
                {typeLabels[video.type]} · {video.duration}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="relative aspect-video w-full overflow-hidden bg-background-50">
          <img
            src={video.cover}
            alt={`${video.title}播放画面`}
            className={`h-full w-full object-cover object-top transition-all duration-700 ${
              playing ? "scale-105 opacity-70" : "scale-100 opacity-45 blur-[1px]"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/20" />

          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-background-50/80 px-3 py-1 backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" />
            <span className="font-label text-[11px] text-foreground-800">演示模式 · 非真实播放</span>
          </div>

          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setPlaying((prev) => !prev)}
              aria-label={playing ? "暂停" : "播放"}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/95 text-background-50 transition-transform hover:scale-105"
            >
              <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl`} />
            </button>
            <p className="max-w-xs px-4 text-center text-xs leading-relaxed text-foreground-700">
              本站暂未接入真实视频流，此界面仅用于展示播放与选集交互效果。
            </p>
          </div>

          <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
            <div className="flex items-center gap-2 text-foreground-800">
              <span className="font-label text-[11px]">{formatTime(progress)}</span>
              <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-background-500/50">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-primary-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="font-label text-[11px]">{video.duration}</span>
            </div>
            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3 text-foreground-700">
                <i className="ri-volume-up-line text-base" />
                <i className="ri-settings-3-line text-base" />
                <i className="ri-fullscreen-line text-base" />
              </div>
              <div className="flex items-center gap-1.5">
                {qualities.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setQuality(item)}
                    className={`whitespace-nowrap rounded px-2 py-1 font-label text-[10px] transition-colors ${
                      quality === item
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100/80 text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-foreground-500">
            导演 {video.director} · 主演 {video.cast.join(" / ")}
          </p>
          <span className="flex items-center gap-1 whitespace-nowrap font-label text-[11px] text-accent-300">
            <i className="ri-information-line text-sm" />
            播放为前端演示，不代表真实片源
          </span>
        </div>
      </div>
    </div>
  );
}